import { useMemo } from 'react';

import { Stack, Typography } from '@mui/material';

import { useDatePickerContext } from './useDatePickerContext';
import { AutocompleteV2 } from '../../controls/dropdownV2';
import { Globe } from '../../icons';
import { IconButton } from '../../buttons';
import { Tooltip } from '../../tooltip';
import { tokens } from '../../../theme/generated/tokens';

const getGMTOffset = (timeZone: string) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'shortOffset',
  });
  const parts = formatter.formatToParts(new Date());
  return parts.find((part) => part.type === 'timeZoneName')?.value || '';
};

const getCityFromTimeZone = (timeZone: string) => {
  const splitZone = timeZone.split('/');
  return splitZone[splitZone.length - 1].replace('_', ' ');
};

const getTimezones = () => {
  const timezones = Intl.supportedValuesOf('timeZone');
  return [
    { label: 'UTC', zone: 'UTC', offset: '' },
    ...timezones.map((zone) => {
      const offset = getGMTOffset(zone);
      const city = getCityFromTimeZone(zone);
      return {
        label: city,
        zone,
        offset: offset === 'GMT' ? 'GMT+0' : offset,
      };
    }),
  ];
};

type TimezonePickerProps = {
  isMini?: boolean;
};

const INPUT_WIDTH = 170;

export const TimezonePicker = ({ isMini = false }: TimezonePickerProps) => {
  const ctx = useDatePickerContext();
  const timezones = useMemo(() => getTimezones(), []);

  return (
    <AutocompleteV2
      testId="timezone-picker"
      sx={{ width: INPUT_WIDTH }}
      prefix={<Globe />}
      value={ctx.timezone}
      onChange={({ selectedItem }) => {
        if (selectedItem) {
          ctx.setTimezone(selectedItem.zone);
        }
      }}
      items={timezones}
      useVirtualization
      popoverSize={{ maxHeight: 200, width: INPUT_WIDTH }}
      getStringValue={(item) => item.zone}
      getLabel={(item) => item.label}
      renderSelectedItem={({ item }) =>
        `${item.label}${item.offset ? `, ${item.offset}` : ''}`
      }
      renderMenuItem={({ item }) => (
        <Stack direction="row" gap={4}>
          <Typography variant="P12M">{item.label}</Typography>
          {item.offset && (
            <Typography
              variant="P10R"
              color={tokens.theme.fg.structural.secondary}
              alignSelf="center"
            >
              ({item.offset})
            </Typography>
          )}
        </Stack>
      )}
      renderToggle={
        isMini
          ? ({ isOpen, toggleButtonProps }) => (
              <Tooltip wrapIf={!isOpen} title={ctx.timezone} placement="top">
                <IconButton {...toggleButtonProps} size="sm" variant="ghost">
                  <Globe color={tokens.theme.fg.structural.secondary} />
                </IconButton>
              </Tooltip>
            )
          : undefined
      }
      size="small"
    />
  );
};
